"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { Checkbox } from "@/components/ui/checkbox";
import { DateInput } from "@/components/ui/date-input";
import { Filters } from "@/components/ui/filters";
import { Icon, type IconName } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipMarked } from "@/components/ui/tooltip";
import type { PipelineExperienceBucket } from "@/lib/api/model/pipelineExperienceBucket";
import type { AutocompleteResult, FilterValue } from "@/lib/api/types";
import { toLocalDayKey } from "@/lib/date/day-keys";
import { cn } from "@/lib/utils";

import { StageDot } from "@/features/jobs/components/stage-dot";
import {
  addEntity,
  addTerm,
  EMPTY_TERM_SET,
  MAX_FILTER_TERMS,
  MAX_FILTER_TERM_LENGTH,
  MAX_GRAD_YEAR,
  MIN_GRAD_YEAR,
  removeEntity,
  removeTerm,
  TIME_PRESETS,
  toAbsoluteToken,
  toggleTermExcluded,
  toggleValue,
  updateEntity,
  type PipelineEntity,
  type PipelineTermSet,
  type PipelineTristate,
} from "@/features/jobs/hooks/use-pipeline-filters";
// The entity cards and the preset launcher are the Talent Search components,
// not copies: a company chip means the same thing in both panels, and a second
// implementation would drift the moment either gains a control.
import { FilterCompanyInput } from "@/features/search/components/filters/input/company-input";
import { FilterUniversityInput } from "@/features/search/components/filters/input/university-input";
import { PresetButton } from "@/features/search/components/filters/shared";

/**
 * A field's label, optionally with a question mark that answers what the name
 * alone can't — what the filter reads from, or what it doesn't mean.
 */
function FieldLabel({
  label,
  tooltip,
}: {
  label: string;
  tooltip?: ReactNode;
}) {
  if (!tooltip) return <Filters.FieldLabel>{label}</Filters.FieldLabel>;
  return (
    <div className="flex items-center gap-1">
      <Filters.FieldLabel>{label}</Filters.FieldLabel>
      <TooltipMarked
        icon="IconQuestionmarkCircle2"
        iconClassName="size-3.5 text-contrast/35 hover:text-contrast/60"
        side="right"
      >
        {tooltip}
      </TooltipMarked>
    </div>
  );
}

/** Section shell — every pipeline filter section renders through this. */
export function PipelineFilterSection({
  icon,
  label,
  defaultOpen,
  activeCount,
  onClear,
  children,
}: {
  icon: IconName;
  label: string;
  defaultOpen?: boolean;
  activeCount?: number;
  onClear?: () => void;
  children: ReactNode;
}) {
  return (
    <Filters.Section
      icon={icon}
      label={label}
      defaultOpen={defaultOpen}
      activeCount={activeCount}
      onClear={onClear}
    >
      <Filters.SectionContent>{children}</Filters.SectionContent>
    </Filters.Section>
  );
}

/**
 * Free-text terms as chips. Enter (or blur) commits the draft; terms within one
 * field are OR-ed by the backend, so several included chips widen the match
 * rather than narrowing it. Each chip can be flipped to excluded, which drops
 * the rows it matches instead.
 */
export function TermChipsField({
  label,
  placeholder,
  value,
  onChange,
  inputType = "text",
  tooltip,
}: {
  label: string;
  placeholder: string;
  value: PipelineTermSet;
  onChange: (next: PipelineTermSet) => void;
  inputType?: "text" | "email" | "url";
  tooltip?: ReactNode;
}) {
  const [draft, setDraft] = useState("");
  const chips = [
    ...value.include.map((term) => ({ term, excluded: false })),
    ...value.exclude.map((term) => ({ term, excluded: true })),
  ];
  const atCap = value.include.length >= MAX_FILTER_TERMS;

  const commit = useCallback(() => {
    const next = addTerm(value, draft);
    setDraft("");
    if (next !== value) onChange(next);
  }, [draft, onChange, value]);

  return (
    <>
      <Filters.FieldContainer>
        <FieldLabel label={label} tooltip={tooltip} />
        <Filters.FieldContent>
          <Filters.TextInput
            type={inputType}
            value={draft}
            enterHint
            maxLength={MAX_FILTER_TERM_LENGTH}
            disabled={atCap}
            placeholder={
              atCap ? `Limit of ${MAX_FILTER_TERMS} reached` : placeholder
            }
            onChange={setDraft}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
          />
        </Filters.FieldContent>
      </Filters.FieldContainer>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <Filters.Chip
              key={chip.term}
              label={chip.term}
              excluded={chip.excluded}
              onToggleExclude={() =>
                onChange(toggleTermExcluded(value, chip.term))
              }
              onRemove={() => onChange(removeTerm(value, chip.term))}
            />
          ))}
          <Filters.ClearChipsButton onClick={() => onChange(EMPTY_TERM_SET)} />
        </div>
      )}
    </>
  );
}

/**
 * Terms with suggestions: the same autocomplete Talent Search uses, and the
 * same escape hatch — a value we have no suggestion for is committed as typed
 * through "Ask Arbi", so the panel never blocks a niche skill or major.
 */
export function AutocompleteTermsField({
  label,
  field,
  value,
  onChange,
  presetLabel,
  onOpenPreset,
  lowercase = false,
  selectValue,
}: {
  label: string;
  /** Autocomplete index — see `useAutocomplete`'s supported fields. */
  field: string;
  value: PipelineTermSet;
  onChange: (next: PipelineTermSet) => void;
  /** Rendered under the input when a preset launcher is wired up. */
  presetLabel?: string;
  onOpenPreset?: () => void;
  /** Suggestions for skills and majors are stored lowercase. */
  lowercase?: boolean;
  /** Narrows a suggestion to the part the filter actually matches on. */
  selectValue?: (result: AutocompleteResult) => string;
}) {
  const commit = useCallback(
    (raw: string) => onChange(addTerm(value, lowercase ? raw.toLowerCase() : raw)),
    [lowercase, onChange, value],
  );

  const chips = [
    ...value.include.map((term) => ({ term, excluded: false })),
    ...value.exclude.map((term) => ({ term, excluded: true })),
  ];

  return (
    <>
      <Filters.FieldContainer>
        <Filters.FieldLabel>{label}</Filters.FieldLabel>
        <Filters.FieldContent className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field={field}
            placeholder="Search..."
            responsive={false}
            flatBottom={Boolean(presetLabel)}
            selectedValues={value.include}
            onSelect={(result: AutocompleteResult) =>
              commit(selectValue ? selectValue(result) : result.value)
            }
            onFreeText={commit}
          />
          {presetLabel && (
            <PresetButton label={presetLabel} onClick={onOpenPreset} />
          )}
        </Filters.FieldContent>
      </Filters.FieldContainer>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <Filters.Chip
              key={chip.term}
              label={chip.term}
              excluded={chip.excluded}
              onToggleExclude={() =>
                onChange(toggleTermExcluded(value, chip.term))
              }
              onRemove={() => onChange(removeTerm(value, chip.term))}
            />
          ))}
          <Filters.ClearChipsButton onClick={() => onChange(EMPTY_TERM_SET)} />
        </div>
      )}
    </>
  );
}

/** Bridges a pipeline entity onto the card's talent-search shape. */
function toFilterValue(entity: PipelineEntity): FilterValue {
  return {
    text: entity.text,
    mode: entity.mode,
    required: entity.required,
    scope: entity.scope,
    domain: entity.domain ?? undefined,
    from_date: entity.from_date ?? undefined,
    to_date: entity.to_date ?? undefined,
    // The card owns the checkbox; we derive its state from the dates so the
    // two can't disagree after a reload (only the dates ride the URL).
    date_enabled: Boolean(entity.from_date || entity.to_date),
  };
}

function fromFilterValue(updated: FilterValue): Partial<PipelineEntity> {
  // Unchecking the card's date box means "any time", so the dates go with it —
  // otherwise a disabled window would still narrow the query.
  const dateEnabled = updated.date_enabled ?? false;
  return {
    mode: updated.mode === "exclude" ? "exclude" : "include",
    required: updated.required ?? false,
    scope: updated.scope ?? "cp",
    from_date: dateEnabled ? (updated.from_date ?? null) : null,
    to_date: dateEnabled ? (updated.to_date ?? null) : null,
  };
}

/**
 * Companies and universities: an autocomplete that also accepts a typed name,
 * a preset launcher, and one card per pick carrying the knobs that name can be
 * matched with (require, exclude, current/past scope, and — for companies — a
 * tenure window).
 */
export function EntityCardsField({
  label,
  field,
  entities,
  onChange,
  presetLabel,
  onOpenPreset,
}: {
  label: string;
  field: "companies" | "universities";
  entities: PipelineEntity[];
  onChange: (next: PipelineEntity[]) => void;
  presetLabel: string;
  onOpenPreset?: () => void;
}) {
  const add = useCallback(
    (input: { text: string; domain?: string | null }) =>
      onChange(addEntity(entities, input)),
    [entities, onChange],
  );

  const patch = useCallback(
    (index: number, updated: FilterValue) =>
      onChange(updateEntity(entities, index, fromFilterValue(updated))),
    [entities, onChange],
  );

  const drop = useCallback(
    (index: number) => onChange(removeEntity(entities, index)),
    [entities, onChange],
  );

  return (
    <>
      <Filters.FieldContainer>
        <Filters.FieldLabel>{label}</Filters.FieldLabel>
        <Filters.FieldContent className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field={field}
            placeholder="Search..."
            responsive={false}
            flatBottom
            selectedValues={entities.map((entity) => entity.text)}
            onSelect={(result: AutocompleteResult) =>
              add({ text: result.value, domain: result.domain })
            }
            onFreeText={(text: string) => add({ text })}
          />
          <PresetButton label={presetLabel} onClick={onOpenPreset} />
        </Filters.FieldContent>
      </Filters.FieldContainer>
      {entities.map((entity, index) =>
        field === "companies" ? (
          <FilterCompanyInput
            key={`${entity.text}-${index}`}
            company={toFilterValue(entity)}
            onChange={(updated) => patch(index, updated)}
            onRemove={() => drop(index)}
          />
        ) : (
          <FilterUniversityInput
            key={`${entity.text}-${index}`}
            university={toFilterValue(entity)}
            onChange={(updated) => patch(index, updated)}
            onRemove={() => drop(index)}
          />
        ),
      )}
      {entities.length > 1 && (
        <div className="flex justify-end">
          <Filters.ClearChipsButton onClick={() => onChange([])} />
        </div>
      )}
    </>
  );
}

export type PickerOption = {
  value: string;
  label: string;
  /** Candidate count from the facets endpoint, when the option is data-derived. */
  count?: number;
  /**
   * Rendered as a swatch on the option row and its chip — a stage's stored
   * colour, or a sequence's id-derived one.
   */
  color?: string | null;
  /** Used in place of the swatch where the value has a mark of its own. */
  icon?: ReactNode;
};

/**
 * Searchable multi-select: pick from the dropdown, and each pick reads back as a
 * chip below it. Used where the vocabulary is the job's own data (stages,
 * sequences) and a checkbox list would be a long scroll to read.
 *
 * Passing `excluded`/`onExcludedChange` adds a per-chip exclude toggle, so the
 * same control can say "these stages" or "everything but these".
 */
export function SearchSelectField({
  label,
  placeholder,
  searchPlaceholder = "Search...",
  emptyMessage,
  noMatchMessage = "No matches",
  options,
  selected,
  onChange,
  excluded,
  onExcludedChange,
  showSwatch = false,
}: {
  label: string;
  placeholder: string;
  searchPlaceholder?: string;
  /** Shown in place of the control when the job has no options at all. */
  emptyMessage?: string;
  noMatchMessage?: string;
  options: PickerOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  excluded?: string[];
  onExcludedChange?: (next: string[]) => void;
  showSwatch?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const excludedValues = useMemo(() => excluded ?? [], [excluded]);

  const byValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const pickedSet = useMemo(
    () => new Set([...selected, ...excludedValues]),
    [selected, excludedValues],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(needle),
    );
  }, [options, query]);

  // A stale selection (option deleted, or a hand-edited URL) still needs a chip
  // so it can be seen and removed — the filter is live on the server either way.
  const chips = [
    ...selected.map((value) => ({ value, isExcluded: false })),
    ...excludedValues.map((value) => ({ value, isExcluded: true })),
  ].map((chip) => ({
    ...chip,
    option: byValue.get(chip.value) ?? { value: chip.value, label: chip.value },
  }));

  // Picking an option that is currently excluded clears the exclusion instead of
  // holding the same value on both sides, which would match nothing.
  const pick = useCallback(
    (value: string) => {
      if (excludedValues.includes(value)) {
        onExcludedChange?.(excludedValues.filter((v) => v !== value));
        return;
      }
      onChange(toggleValue(selected, value));
    },
    [excludedValues, onChange, onExcludedChange, selected],
  );

  const flip = useCallback(
    (value: string, isExcluded: boolean) => {
      if (!onExcludedChange) return;
      if (isExcluded) {
        onExcludedChange(excludedValues.filter((v) => v !== value));
        onChange(toggleValue(selected, value));
        return;
      }
      onChange(selected.filter((v) => v !== value));
      onExcludedChange(toggleValue(excludedValues, value));
    },
    [excludedValues, onChange, onExcludedChange, selected],
  );

  const clear = useCallback(() => {
    onChange([]);
    onExcludedChange?.([]);
  }, [onChange, onExcludedChange]);

  return (
    <>
      <Filters.FieldContainer>
        <Filters.FieldLabel>{label}</Filters.FieldLabel>
        <Filters.FieldContent>
          {options.length === 0 && emptyMessage ? (
            <p className="text-xs text-contrast/45">{emptyMessage}</p>
          ) : (
            <Select.Root
              open={open}
              onOpenChange={(next) => {
                setOpen(next);
                if (!next) setQuery("");
              }}
              // Always a popover: on mobile the panel is itself a drawer, and a
              // nested drawer would close it on the first pick.
              responsive={false}
            >
              <Select.Trigger variant="filter" className="w-full">
                <span className="min-w-0 flex-1 truncate text-left text-sm text-contrast/50">
                  {placeholder}
                </span>
                <Select.Icon className="ml-auto" />
              </Select.Trigger>
              <Select.Content>
                <Select.Search
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <Select.List>
                  {matches.length === 0 ? (
                    <Select.ListEmpty>{noMatchMessage}</Select.ListEmpty>
                  ) : (
                    matches.map((option, index) => {
                      const isSelected = pickedSet.has(option.value);
                      return (
                        <Select.Item
                          key={option.value}
                          index={index}
                          selected={isSelected}
                          onSelect={() => pick(option.value)}
                        >
                          {option.icon ??
                            (showSwatch && <StageDot color={option.color} />)}
                          <Select.ItemLabel selected={isSelected}>
                            {option.label}
                          </Select.ItemLabel>
                          {option.count !== undefined && (
                            <Select.ItemCount>{option.count}</Select.ItemCount>
                          )}
                          {isSelected && <Select.ItemCheck className="ml-1.5" />}
                        </Select.Item>
                      );
                    })
                  )}
                </Select.List>
              </Select.Content>
            </Select.Root>
          )}
        </Filters.FieldContent>
      </Filters.FieldContainer>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <Filters.Chip
              key={chip.value}
              label={chip.option.label}
              excluded={chip.isExcluded}
              iconBefore={
                chip.option.icon ??
                (showSwatch ? (
                  <StageDot color={chip.option.color} iconSize={12} />
                ) : undefined)
              }
              onToggleExclude={
                onExcludedChange
                  ? () => flip(chip.value, chip.isExcluded)
                  : undefined
              }
              onRemove={() =>
                chip.isExcluded
                  ? onExcludedChange?.(
                      excludedValues.filter((value) => value !== chip.value),
                    )
                  : onChange(selected.filter((value) => value !== chip.value))
              }
            />
          ))}
          <Filters.ClearChipsButton onClick={clear} />
        </div>
      )}
    </>
  );
}

/**
 * Single-select over a fixed vocabulary, with the empty option folded in so the
 * filter can be cleared from the same list it was set from.
 */
export function SingleSelectField<T extends string>({
  label,
  placeholder,
  options,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (next: T | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((option) => option.value === value);

  return (
    <Filters.FieldContainer>
      <Filters.FieldLabel>{label}</Filters.FieldLabel>
      <Filters.FieldContent>
        <Select.Root open={open} onOpenChange={setOpen} responsive={false}>
          <Select.Trigger variant="filter" className="w-full">
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-left text-sm",
                current ? "text-heading" : "text-contrast/50",
              )}
            >
              {current?.label ?? placeholder}
            </span>
            <Select.Icon className="ml-auto" />
          </Select.Trigger>
          <Select.Content>
            <Select.List>
              <Select.Item
                index={0}
                selected={value === null}
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Select.ItemLabel selected={value === null}>
                  {placeholder}
                </Select.ItemLabel>
                {value === null && <Select.ItemCheck className="ml-1.5" />}
              </Select.Item>
              {options.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <Select.Item
                    key={option.value}
                    index={index + 1}
                    selected={isSelected}
                    onSelect={() => {
                      onChange(isSelected ? null : option.value);
                      setOpen(false);
                    }}
                  >
                    <Select.ItemLabel selected={isSelected}>
                      {option.label}
                    </Select.ItemLabel>
                    {isSelected && <Select.ItemCheck className="ml-1.5" />}
                  </Select.Item>
                );
              })}
            </Select.List>
          </Select.Content>
        </Select.Root>
      </Filters.FieldContent>
    </Filters.FieldContainer>
  );
}

export type CheckboxOption<T extends string> = {
  value: T;
  label: string;
  /** Candidate count from the facets endpoint, when the option is data-derived. */
  count?: number;
  /** The value's own glyph — the status/origin icons used elsewhere in the app. */
  icon?: ReactNode;
};

/** Multi-select over a fixed (or facet-derived) vocabulary. */
export function CheckboxOptionList<T extends string>({
  options,
  selected,
  onChange,
  emptyMessage,
}: {
  options: CheckboxOption<T>[];
  selected: T[];
  onChange: (next: T[]) => void;
  emptyMessage?: string;
}) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  if (options.length === 0 && emptyMessage) {
    return <p className="text-xs text-contrast/45">{emptyMessage}</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {options.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center gap-1.5 py-0.5 text-sm text-contrast/80 hover:text-heading"
        >
          <Checkbox
            checked={selectedSet.has(option.value)}
            aria-label={option.label}
            onChange={() => onChange(toggleValue(selected, option.value))}
          />
          {option.icon && (
            <span className="flex size-4 shrink-0 items-center justify-center">
              {option.icon}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">{option.label}</span>
          {option.count !== undefined && (
            <span className="shrink-0 text-xs tabular-nums text-contrast/40">
              {option.count}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}

const TRISTATE_TABS = [
  { value: "any", label: "Any", icon: "IconListBullets" },
  { value: "yes", label: "Yes", icon: "IconCircleCheck" },
  { value: "no", label: "No", icon: "IconCircleMinus" },
] as const satisfies readonly { value: string; label: string; icon: IconName }[];

/**
 * Any / Yes / No over a nullable boolean. "Any" is the absence of the filter,
 * not `false` — the difference matters for fields like "Has email", where `no`
 * is itself a useful (and expensive to reproduce) query.
 */
export function TristateField({
  label,
  value,
  onChange,
  hint,
  tooltip,
}: {
  label: string;
  value: PipelineTristate | null;
  onChange: (next: PipelineTristate | null) => void;
  /** One line under the control, for a field whose name isn't self-explanatory. */
  hint?: string;
  /** Behind a question mark, for a field whose name invites a wrong reading. */
  tooltip?: ReactNode;
}) {
  return (
    <Filters.FieldContainer>
      <FieldLabel label={label} tooltip={tooltip} />
      <Filters.FieldContent>
        <Tabs
          value={value ?? "any"}
          onValueChange={(next) => onChange(next === "any" ? null : (next as PipelineTristate))}
          variant="inset"
          className="w-full"
        >
          {/* h-9.5 matches the tasks sidebar's read-state segmented control,
              which is the same shape of choice. */}
          <TabsList
            aria-label={label}
            className="h-9.5 w-full gap-1 rounded-md border border-border bg-surface-inset p-1"
          >
            {TRISTATE_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                <Icon name={tab.icon} size={14} />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {hint && <p className="mt-1 text-xs text-contrast/45">{hint}</p>}
      </Filters.FieldContent>
    </Filters.FieldContainer>
  );
}

export function parseDay(token: string | null): Date | undefined {
  if (!token) return undefined;
  const [year, month, day] = token.slice(0, 10).split("-").map(Number);
  // Noon avoids the value shifting a day when rendered in a negative-offset zone.
  const date = new Date(year, month - 1, day, 12);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function formatDay(date: Date | undefined | null): string | null {
  if (!date) return null;
  return toLocalDayKey(date);
}

const PRESET_LABELS = new Map(
  TIME_PRESETS.map((preset) => [`now-${preset.window}` as string, preset.label]),
);

/**
 * What a relative bound reads as. Every quick range is here by name; a window
 * only a saved link can carry ("now-14d") still reads as the span it is.
 */
function relativeLabel(token: string): string {
  if (token === "now") return "Now";
  const preset = PRESET_LABELS.get(token);
  if (preset) return preset;
  const window = token.slice("now-".length);
  const amount = window.slice(0, -1);
  return window.endsWith("h") ? `Last ${amount} hours` : `Last ${amount} days`;
}

const RANGE_OPTIONS: { label: string; from: string | null }[] = [
  { label: "All time", from: null },
  ...TIME_PRESETS.map((preset) => ({
    label: preset.label,
    from: `now-${preset.window}`,
  })),
];

/**
 * A time range, as a quick range over a from/to pair.
 *
 * The quick range is not a filter of its own: picking one writes the bounds
 * below it, the way Splunk's presets fill earliest/latest. What it writes is
 * *relative* ("now-7d"), so a saved view still means "the last week" next week —
 * and each bound carries a clock, not just a day, so a range can be an afternoon.
 *
 * An empty lower bound is the beginning of the pipeline; an empty upper bound is
 * now, and stays now as time passes — a quick range is one of these, so it reads
 * back as one only while the upper end is open. Typing bounds that aren't a
 * quick range is the custom case, and the picker says so rather than pretending
 * one of the presets is still in force.
 */
export function TimeRangeField({
  label,
  from,
  to,
  onChange,
}: {
  /** Names the thing being timed ("Added", "Entered stage") for screen readers. */
  label: string;
  from: string | null;
  to: string | null;
  onChange: (next: { from: string | null; to: string | null }) => void;
}) {
  const setBound = useCallback(
    (edge: "from" | "to", next: string | null) => {
      if (edge === "from") return onChange({ from: next, to });
      // A fixed upper bound against a relative lower one is a range whose width
      // changes every minute; pin the lower bound where it currently points so
      // the range the user just closed is the one they keep.
      return onChange({
        from: next === null ? from : toAbsoluteToken(from),
        to: next,
      });
    },
    [from, onChange, to],
  );

  return (
    <>
      <QuickRangeSelect
        label={label}
        from={from}
        to={to}
        onChange={(next) => onChange({ from: next, to: null })}
      />

      <div className="grid grid-cols-2 gap-2">
        <InstantBound
          label={`${label} from`}
          caption="From"
          value={from}
          emptyLabel="Any time"
          onChange={(next) => setBound("from", next)}
        />
        <InstantBound
          label={`${label} to`}
          caption="To"
          value={to}
          emptyLabel="Now"
          onChange={(next) => setBound("to", next)}
        />
      </div>
    </>
  );
}

/** The spans, as one choice: the range the bounds below currently describe. */
function QuickRangeSelect({
  label,
  from,
  to,
  onChange,
}: {
  label: string;
  from: string | null;
  to: string | null;
  onChange: (from: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  // A relative lower bound against an open upper one — anything else is a range
  // the presets can't express, whether it was typed here or arrived in a link.
  const span = to === null && from !== null ? from : null;
  const allTime = from === null && to === null;
  const current = allTime ? "All time" : span ? relativeLabel(span) : "Custom";

  return (
    <Filters.FieldContainer>
      <Filters.FieldLabel>Quick Range</Filters.FieldLabel>
      <Filters.FieldContent>
        <Select.Root open={open} onOpenChange={setOpen} responsive={false}>
          <Select.Trigger
            variant="filter"
            className="w-full"
            aria-label={`${label} quick range`}
            // Reads as set unless it spans everything, the way the bounds beside
            // it do — "All time" is the absence of a range, not a choice of one.
            active={!allTime}
          >
            <Icon name="IconClock" size={14} className="shrink-0" />
            <Select.TriggerContent>{current}</Select.TriggerContent>
            <Select.Icon className="ml-auto" />
          </Select.Trigger>
          <Select.Content>
            <Select.List>
              {RANGE_OPTIONS.map((option, index) => {
                const selected =
                  option.from === null ? allTime : option.from === span;
                return (
                  <Select.Item
                    key={option.label}
                    index={index}
                    selected={selected}
                    onSelect={() => {
                      onChange(option.from);
                      setOpen(false);
                    }}
                  >
                    <Select.ItemLabel selected={selected}>
                      {option.label}
                    </Select.ItemLabel>
                    {selected && <Select.ItemCheck className="ml-1.5" />}
                  </Select.Item>
                );
              })}
            </Select.List>
          </Select.Content>
        </Select.Root>
      </Filters.FieldContent>
    </Filters.FieldContainer>
  );
}

/**
 * One end of a time range: a day and a time of day. A relative bound shows the
 * quick range that set it, and picking a day off the calendar is what turns it
 * into a fixed one.
 */
function InstantBound({
  label,
  caption,
  value,
  emptyLabel,
  onChange,
}: {
  label: string;
  caption: string;
  value: string | null;
  /** What this end means while unset — the open end of the range. */
  emptyLabel: string;
  onChange: (next: string | null) => void;
}) {
  const relative = value !== null && value.startsWith("now");
  const day = relative ? null : value;
  const time = day?.slice(11) ?? "";

  return (
    <Filters.FieldContainer>
      <Filters.FieldLabel>{caption}</Filters.FieldLabel>
      <Filters.FieldContent className="flex flex-col gap-1">
        <DateInput
          variant="filter"
          aria-label={label}
          placeholder={relative ? relativeLabel(value!) : emptyLabel}
          // A relative bound has no date to show, but it is still a bound: it
          // reads as set rather than as the empty end of the range.
          active={value !== null}
          value={parseDay(day)}
          onChange={(next) => {
            const nextDay = formatDay(next);
            if (!nextDay) return onChange(null);
            onChange(time ? `${nextDay}T${time}` : nextDay);
          }}
        />
        <Input
          type="time"
          variant="filter"
          size="sm"
          aria-label={`${label} time of day`}
          // A time needs a day to sit on, and a relative bound already carries
          // its own; the whole day is the default until one is typed.
          disabled={day === null}
          value={time}
          onChange={(e) => {
            if (!day) return;
            const nextTime = e.target.value;
            onChange(nextTime ? `${day.slice(0, 10)}T${nextTime}` : day.slice(0, 10));
          }}
        />
      </Filters.FieldContent>
    </Filters.FieldContainer>
  );
}

/** Tallest bar in the distribution, in px — matches the bar row's `h-10`. */
const HISTOGRAM_HEIGHT = 40;
/** An empty bucket still draws a sliver, so the years axis reads as continuous. */
const EMPTY_BUCKET_HEIGHT = 1;
/**
 * How long the range sits still before it becomes a query. A drag crosses dozens
 * of values and every one of them would otherwise be a request; this is short
 * enough that letting go feels like the filter applied instantly.
 */
const RANGE_COMMIT_DELAY_MS = 200;
/** Domain when no candidate's experience is known — nothing to size it against. */
const FALLBACK_DOMAIN_YEARS = 40;

/**
 * Years of experience as a range over the pipeline's own distribution: the
 * histogram is what the job actually holds, the bars inside the range carry the
 * panel's accent, and the two ends are typeable for an exact bound.
 *
 * The domain is the job's own range (the top of the last bar), not a fixed span,
 * and both ends mean "no bound" rather than a number: a handle resting at the
 * top matches a 60-year career the same as a 6-year one, and the section stays
 * uncounted. `capYears` only limits what a *typed* bound may say.
 *
 * Moving the range is local until it settles — dragging renders every step but
 * queries none of them.
 */
export function ExperienceRangeField({
  min,
  max,
  capYears,
  buckets,
  onChange,
}: {
  min: number | null;
  max: number | null;
  capYears: number;
  /** Empty hides the histogram and leaves just the range. */
  buckets: PipelineExperienceBucket[];
  onChange: (next: { min: number | null; max: number | null }) => void;
}) {
  const ids = useId();
  const [pending, setPending] = useState<[number, number] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applied = useRef({ min, max });

  const drop = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPending(null);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  // The applied filter is the truth. A change from anywhere else — Clear all, the
  // section's own clear, a preset, the back button — drops an unsettled move
  // instead of being undone by it once the delay elapses.
  useEffect(() => {
    if (applied.current.min === min && applied.current.max === max) return;
    applied.current = { min, max };
    drop();
  }, [min, max, drop]);

  const domainTop = Math.min(
    capYears,
    Math.max(
      buckets.at(-1)?.max_years ?? FALLBACK_DOMAIN_YEARS,
      // A bound typed past the job's own range still has to be reachable.
      min ?? 0,
      max ?? 0,
    ),
  );

  // Read the pair in order: a hand-edited URL can invert it, and the slider
  // would have nowhere sane to put the handles.
  const [lower, upper] = pending ?? [
    Math.min(min ?? 0, max ?? domainTop),
    Math.max(min ?? 0, max ?? domainTop),
  ];

  const peak = useMemo(
    () => Math.max(0, ...buckets.map((bucket) => bucket.count)),
    [buckets],
  );

  const commit = useCallback(
    (nextLower: number, nextUpper: number) => {
      drop();
      onChange({
        min: nextLower <= 0 ? null : nextLower,
        max: nextUpper >= domainTop ? null : nextUpper,
      });
    },
    [domainTop, drop, onChange],
  );

  /** Show the move now, query it once it stops. */
  const commitWhenSettled = useCallback(
    (nextLower: number, nextUpper: number) => {
      setPending([nextLower, nextUpper]);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(
        () => commit(nextLower, nextUpper),
        RANGE_COMMIT_DELAY_MS,
      );
    },
    [commit],
  );

  return (
    <div className="flex flex-col gap-2">
      {peak > 0 && (
        <div aria-hidden className="flex h-10 items-end gap-[2px]">
          {buckets.map((bucket) => {
            // In range when the bucket covers a selected year: it starts at or
            // below the top, and reaches past the bottom.
            const inRange =
              bucket.min_years <= upper && bucket.max_years > lower;
            const height =
              bucket.count === 0
                ? EMPTY_BUCKET_HEIGHT
                : Math.max(2, Math.round((bucket.count / peak) * HISTOGRAM_HEIGHT));
            return (
              <div
                key={bucket.min_years}
                data-slot="experience-bucket"
                data-in-range={inRange}
                title={`${bucket.min_years}–${bucket.max_years} years · ${bucket.count}`}
                style={{ height }}
                className={cn(
                  "min-w-0 flex-1 rounded-t-[1px]",
                  inRange ? "bg-[#ffaf53]" : "bg-contrast/15",
                )}
              />
            );
          })}
        </div>
      )}

      {/* Pulled up so the rail sits at the foot of the bars and the handles
          overlay them, as designed. */}
      <Slider
        variant="filter"
        value={[lower, upper]}
        min={0}
        max={domainTop}
        step={1}
        minStepsBetweenThumbs={0}
        thumbLabels={["Minimum years of experience", "Maximum years of experience"]}
        onValueChange={([nextLower, nextUpper]) =>
          commitWhenSettled(nextLower, nextUpper)
        }
        className={cn(peak > 0 && "-mt-4")}
      />

      <div className="flex items-start justify-between">
        <RangeEndInput
          id={`${ids}-min`}
          label="Min Years"
          value={lower}
          domainTop={domainTop}
          // Emptied means "no lower bound", which is the bottom of the domain:
          // the field can't show blank, the handle has to sit somewhere. Either
          // end pushes the other rather than crossing it.
          onType={(next) => commitWhenSettled(next ?? 0, Math.max(next ?? 0, upper))}
          onSettle={() => commit(lower, upper)}
        />
        <RangeEndInput
          id={`${ids}-max`}
          label="Max Years"
          value={upper}
          domainTop={domainTop}
          onType={(next) =>
            commitWhenSettled(Math.min(next ?? domainTop, lower), next ?? domainTop)
          }
          onSettle={() => commit(lower, upper)}
        />
      </div>
    </div>
  );
}

/**
 * One end of the experience range, labelled beneath itself so the label doesn't
 * come between the range and the numbers it produced.
 */
function RangeEndInput({
  id,
  label,
  value,
  domainTop,
  onType,
  onSettle,
}: {
  id: string;
  label: string;
  value: number;
  domainTop: number;
  /** `null` when the field is emptied — this end has no bound of its own. */
  onType: (next: number | null) => void;
  /** The number is finished: apply it without waiting for the range to settle. */
  onSettle: () => void;
}) {
  return (
    <div className="flex w-[73px] flex-col gap-1">
      <Input
        id={id}
        type="number"
        variant="filter"
        size="none"
        min={0}
        max={domainTop}
        step={1}
        value={value}
        className="h-[30px] rounded-md px-2 text-center text-[13px]"
        // Always populated, so typing should replace the bound rather than
        // append a digit to it.
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onType(null);
            return;
          }
          const parsed = Number.parseInt(raw, 10);
          if (Number.isNaN(parsed)) return;
          // Clamped to the range on offer: a bound past the longest career on the
          // job selects exactly what the end of the range does.
          onType(Math.min(Math.max(parsed, 0), domainTop));
        }}
        // Two digits arrive as two keystrokes; leaving or confirming the field
        // says the number is finished.
        onBlur={onSettle}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSettle();
        }}
      />
      <label htmlFor={id} className="text-center text-[13px] text-contrast/50">
        {label}
      </label>
    </div>
  );
}

/**
 * How far either side of this year the graduation-year lists reach. The same
 * spans Talent Search offers, and for the same reason: a class of 2032 is in the
 * pipeline as a student, and 75 years back covers anyone still working.
 */
const GRAD_YEAR_PAST_OFFSET = 75;
const GRAD_YEAR_FUTURE_OFFSET = 5;

/** Years to pick from, newest first — the end most selections are near. */
function useGradYears(): number[] {
  return useMemo(() => {
    const now = new Date().getFullYear();
    const newest = Math.min(now + GRAD_YEAR_FUTURE_OFFSET, MAX_GRAD_YEAR);
    const oldest = Math.max(now - GRAD_YEAR_PAST_OFFSET, MIN_GRAD_YEAR);
    const years: number[] = [];
    for (let year = newest; year >= oldest; year -= 1) years.push(year);
    return years;
  }, []);
}

/**
 * A graduation-year range, as "between any and any" — the same control Talent
 * Search uses, because it is the same question asked of the same data.
 *
 * Picking a bound past the other one carries that one along rather than leaving
 * an inverted range: `2020 to 2015` matches nobody, and the compiler drops it
 * outright, so the panel would be showing a filter that isn't running.
 */
export function GradYearRangeField({
  start,
  end,
  onChange,
}: {
  start: number | null;
  end: number | null;
  onChange: (next: { start: number | null; end: number | null }) => void;
}) {
  const years = useGradYears();

  return (
    <Filters.FieldContainer>
      <Filters.FieldLabel>Graduation Year</Filters.FieldLabel>
      <Filters.FieldContent className="flex min-w-0 items-center">
        <span className="mr-1.5 shrink-0 text-sm text-contrast/80">Between</span>
        <YearSelect
          label="Graduation year from"
          value={start}
          years={years}
          onChange={(next) =>
            onChange({
              start: next,
              end: next !== null && end !== null && next > end ? next : end,
            })
          }
        />
        <span className="mx-1.5 shrink-0 text-sm text-contrast/80">and</span>
        <YearSelect
          label="Graduation year to"
          value={end}
          years={years}
          onChange={(next) =>
            onChange({
              start: next !== null && start !== null && next < start ? next : start,
              end: next,
            })
          }
        />
      </Filters.FieldContent>
    </Filters.FieldContainer>
  );
}

/** One end of the range: a year, or "Any" for the end left open. */
function YearSelect({
  label,
  value,
  years,
  onChange,
}: {
  label: string;
  value: number | null;
  years: number[];
  onChange: (next: number | null) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-w-0 flex-1">
      <Select.Root open={open} onOpenChange={setOpen} responsive={false}>
        <Select.Trigger
          variant="filter"
          className="w-full"
          aria-label={label}
          active={value !== null}
        >
          <Select.TriggerContent>{value ?? "Any"}</Select.TriggerContent>
          <Select.Icon className="ml-auto" />
        </Select.Trigger>
        <Select.Content className="max-h-60">
          <Select.List>
            <Select.Item
              index={0}
              selected={value === null}
              onSelect={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <Select.ItemLabel selected={value === null}>Any</Select.ItemLabel>
              {value === null && <Select.ItemCheck className="ml-1.5" />}
            </Select.Item>
            {years.map((year, index) => {
              const selected = year === value;
              return (
                <Select.Item
                  key={year}
                  index={index + 1}
                  selected={selected}
                  onSelect={() => {
                    onChange(year);
                    setOpen(false);
                  }}
                >
                  <Select.ItemLabel selected={selected}>{year}</Select.ItemLabel>
                  {selected && <Select.ItemCheck className="ml-1.5" />}
                </Select.Item>
              );
            })}
          </Select.List>
        </Select.Content>
      </Select.Root>
    </div>
  );
}
